import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { getCurrencyDisplayName } from '../utils/currencyHelper';

import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import PortfolioChart from '../components/PortfolioChart';
import ProfitSimulator from '../components/ProfitSimulator';
import AssetComparison from '../components/AssetComparison';
import GoalsWidget from '../components/GoalsWidget';
import CurrencyTool from '../components/CurrencyTool';

// DnD Imports hiding FaExchangeAlt
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaFileExcel, FaStickyNote, FaGripVertical, FaExchangeAlt, FaEdit, FaCoins, FaChartLine, FaEnvelope } from 'react-icons/fa';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Sortable Row Component
const SortableRow = ({ item, getTypeName, deleteItem, updateNote, formatPrice, onCompare }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className={`hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0 ${isDragging ? 'bg-indigo-50 dark:bg-gray-600' : ''}`}>
      <td className="px-2 py-4 text-center text-gray-300 cursor-move w-8" {...attributes} {...listeners}>
        <FaGripVertical className="hover:text-gray-600" />
      </td>
      <td className="px-3 py-4 whitespace-nowrap">
        <div className="flex flex-col">
          <span className="text-sm font-bold text-slate-800 dark:text-white">
            {getTypeName(item.type)}
          </span>
          {item.note ? (
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1 group cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => updateNote(item._id, item.note)} title={item.note}>
              <FaStickyNote className="text-[10px]" />
              <span className="truncate max-w-[100px]">{item.note}</span>
              <FaEdit className="text-[10px] opacity-0 group-hover:opacity-100" />
            </div>
          ) : (
            <div className="text-xs text-slate-400 mt-1 flex items-center gap-1 group cursor-pointer hover:text-indigo-600 transition-colors opacity-0 group-hover:opacity-100" onClick={() => updateNote(item._id, '')}>
              <FaEdit className="text-[10px]" /> <span>Not ekle</span>
            </div>
          )}
        </div>
      </td>

      <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-gray-300 text-right font-medium">
        {item.amount}
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-gray-300 text-right">
        {formatPrice(item.buyPrice)}
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-gray-100 text-right font-semibold">
        {formatPrice(item.currentPrice)}
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-gray-400 text-right">
        {formatPrice(item.totalCost)}
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white text-right font-bold">
        {formatPrice(item.currentValue)}
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-right">
        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-bold ${item.profitLoss >= 0 ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
          {item.profitLoss >= 0 ? <FaArrowUp className="text-[10px]" /> : <FaArrowDown className="text-[10px]" />}
          <span>
            {formatPrice(Math.abs(item.profitLoss))}
          </span>
          <span className="opacity-80">
            ({parseFloat(item.profitLossPercent).toFixed(1)}%)
          </span>
        </div>
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-gray-400 text-right font-medium">
        {format(new Date(item.buyDate), 'dd MMM yyyy', { locale: tr })}
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end space-x-1">
          <button
            onClick={() => onCompare(item)}
            className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
            title="Karşılaştır"
          >
            <FaExchangeAlt />
          </button>
          <button
            onClick={() => deleteItem(item._id)}
            className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors"
            title="Sil"
          >
            <FaTrash />
          </button>
        </div>
      </td>
    </tr >
  );
};

const Portfolio = () => {
  const { loading: authLoading } = useAuth();
  const { formatPrice, displayCurrency, setDisplayCurrency } = useCurrency();
  const [portfolio, setPortfolio] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAssetForComparison, setSelectedAssetForComparison] = useState(null);
  const [reportSettings, setReportSettings] = useState({ enabled: false, frequency: 'weekly' });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    fetchPortfolio();
    fetchStats();
    fetchReportSettings();
  }, [authLoading]);

  const fetchReportSettings = async () => {
    try {
      const { data } = await axios.get('/api/portfolio/report-settings');
      setReportSettings(data);
    } catch (error) {
      console.error('Rapor ayarları yüklenemedi:', error);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const { data } = await axios.get('/api/portfolio');
      setPortfolio(data);
      setLoading(false);
    } catch (error) {
      console.error('Portföy yüklenemedi:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get('/api/portfolio/stats/summary');
      setStats(data);
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error);
      setStats(null);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Bu varlığı silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`/api/portfolio/${id}`);
      fetchPortfolio();
      fetchStats();
    } catch (error) {
      console.error('Silme hatası:', error);
    }
  };

  const updateNote = async (id, currentNote) => {
    const newNote = window.prompt("Notunuzu girin:", currentNote || "");
    if (newNote === null) return; // Cancelled

    try {
      await axios.put(`/api/portfolio/${id}`, { note: newNote });
      // Optimistic update or refresh
      fetchPortfolio();
    } catch (error) {
      console.error('Not güncelleme hatası:', error);
      alert("Not güncellenemedi.");
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setPortfolio((items) => {
        const oldIndex = items.findIndex((i) => i._id === active.id);
        const newIndex = items.findIndex((i) => i._id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Persist order
        axios.post('/api/portfolio/reorder', { newOrder: newItems.map(i => i._id) })
          .catch(err => console.error('Reorder error:', err));

        return newItems;
      });
    }
  };

  const getTypeName = (type) => {
    return getCurrencyDisplayName(type);
  };

  const exportToExcel = () => {
    if (portfolio.length === 0) return;

    const exportData = portfolio.map(item => ({
      'Varlık Türü': getTypeName(item.type),
      'Miktar': item.amount,
      'Not': item.note || '',
      'Alış Fiyatı (₺)': item.buyPrice,
      'Güncel Fiyat (₺)': item.currentPrice,
      'Toplam Maliyet (₺)': item.totalCost,
      'Güncel Değer (₺)': item.currentValue,
      'Kar/Zarar (₺)': item.profitLoss,
      'Getiri (%)': parseFloat(item.profitLossPercent).toFixed(2),
      'Alış Tarihi': format(new Date(item.buyDate), 'dd.MM.yyyy', { locale: tr })
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Portföy');

    // Auto trim handled by library mostly, but we can set col width
    ws['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 12 }];

    XLSX.writeFile(wb, `Portfoy_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 py-12 transition-colors duration-300 ease-in-out font-sans">
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 min-w-0">

        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Varlık Portföyü</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Yatırımlarınızın güncel durumunu takip edin</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Currency Toggle */}
            <select
              value={displayCurrency}
              onChange={(e) => setDisplayCurrency(e.target.value)}
              className="px-4 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 hover:border-indigo-500 text-sm font-semibold text-slate-700 dark:text-gray-200 cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            >
              <option value="TRY">₺ TRY</option>
              <option value="USD">$ USD</option>
              <option value="EUR">€ EUR</option>
            </select>

            {/* Email Report Toggle with Dropdown */}
            <div className="relative group">
              <button
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all shadow-sm font-medium ${reportSettings.enabled
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-white/10 text-slate-700 dark:text-gray-200 hover:border-indigo-500'
                  }`}
              >
                <FaEnvelope className={reportSettings.enabled ? 'text-white' : 'text-indigo-500'} />
                <span className="hidden sm:inline">
                  {reportSettings.enabled
                    ? `${reportSettings.frequency === 'weekly' ? 'Haftalık' : 'Aylık'} Abonelik`
                    : 'E-posta Aboneliği'}
                </span>
              </button>

              {/* Dropdown Menu */}
              {/* Dropdown Menu */}
              <div className="absolute top-full right-0 mt-2 w-72 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 overflow-hidden">

                {/* Development Overlay */}
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-[2px]">
                  <span className="px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-full shadow-lg transform -rotate-2 border-2 border-white dark:border-slate-800">
                    🚧 Geliştirme Aşamasında
                  </span>
                </div>

                {/* Blurred & Disabled Content */}
                <div className="p-4 filter blur-[1px] pointer-events-none select-none opacity-60">
                  <div className="text-sm font-semibold text-slate-800 dark:text-white mb-2">📧 E-posta Rapor Aboneliği</div>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-4">
                    Portföy özetinizi, kar/zarar durumunuzu ve varlık değerlerinizi düzenli olarak e-posta ile alın.
                  </p>

                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-600 dark:text-gray-300">Sıklık:</span>
                    <select
                      value={reportSettings.frequency}
                      disabled
                      className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-not-allowed"
                    >
                      <option value="weekly">Haftalık (Pazartesi)</option>
                      <option value="monthly">Aylık (Ayın 1'i)</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600 dark:text-gray-300">Abonelik:</span>
                    <button
                      disabled
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-gray-300 dark:bg-gray-600 cursor-not-allowed`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow translate-x-1`}
                      />
                    </button>
                  </div>

                  {reportSettings.enabled && (
                    <p className="text-xs text-green-600 dark:text-green-400 text-center pt-2 border-t border-gray-100 dark:border-gray-700">
                      ✓ Aktif - {reportSettings.frequency === 'weekly' ? 'Her Pazartesi' : 'Her ayın 1\'i'} 09:00
                    </p>
                  )}
                </div>
              </div>
            </div>

            {portfolio.length > 0 && (
              <button
                onClick={exportToExcel}
                className="flex items-center space-x-2 bg-emerald-600 text-white px-5 py-2.5 rounded-xl hover:bg-emerald-700 transition shadow-sm hover:shadow active:scale-95 font-medium"
              >
                <FaFileExcel className="text-lg" />
                <span>Excel</span>
              </button>
            )}

            <Link
              to="/add-asset"
              className="flex items-center space-x-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition shadow-indigo-200 shadow-lg hover:shadow-xl active:scale-95 font-medium"
            >
              <FaPlus />
              <span>Varlık Ekle</span>
            </Link>
          </div>
        </div>

        {/* Summary Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-white/10 relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity">
                <FaCoins className="text-6xl text-slate-900 dark:text-white" />
              </div>
              <p className="text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wider mb-2">Toplam Yatırım</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">
                {formatPrice(stats.totalInvestment)}
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-white/10 relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity">
                <FaChartLine className="text-6xl text-indigo-600" />
              </div>
              <p className="text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wider mb-2">Güncel Değer</p>
              <p className="text-3xl font-bold text-indigo-900 dark:text-indigo-100 tracking-tight leading-none">
                {formatPrice(stats.currentValue)}
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-white/10 relative overflow-hidden group hover:shadow-md transition-shadow">
              <p className="text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wider mb-2">Net Kar/Zarar</p>
              <div className="flex items-baseline gap-2">
                <p className={`text-3xl font-bold tracking-tight leading-none ${parseFloat(stats.totalProfitLoss) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {parseFloat(stats.totalProfitLoss) >= 0 ? '+' : ''}
                  {formatPrice(stats.totalProfitLoss)}
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-white/10 relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity">
                {parseFloat(stats.totalProfitLossPercent) >= 0 ? <FaArrowUp className="text-6xl text-emerald-600" /> : <FaArrowDown className="text-6xl text-rose-600" />}
              </div>
              <p className="text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wider mb-2">Getiri Oranı</p>
              <p className={`text-3xl font-bold tracking-tight leading-none ${parseFloat(stats.totalProfitLossPercent) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {parseFloat(stats.totalProfitLossPercent) >= 0 ? '+' : ''}
                {parseFloat(stats.totalProfitLossPercent).toFixed(2)}%
              </p>
            </div>
          </div>
        )}

        <div className="mb-10">
          <CurrencyTool compact showTitle defaultFrom="USD" defaultTo="TRY" />
        </div>

        {/* Portfolio Table */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
          </div>
        ) : portfolio.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-16 text-center border border-gray-100 dark:border-white/10">
            <div className="w-20 h-20 bg-indigo-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaStickyNote className="text-3xl text-indigo-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Henüz Varlık Eklenmemiş</h3>
            <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-8">Portföyünüzü oluşturmaya başlamak için ilk varlığınızı ekleyin.</p>
            <Link
              to="/add-asset"
              className="inline-flex items-center space-x-2 bg-indigo-600 text-white px-8 py-4 rounded-xl hover:bg-indigo-700 transition shadow-lg hover:shadow-indigo-200 font-semibold"
            >
              <FaPlus />
              <span>İlk Varlığı Ekle</span>
            </Link>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm overflow-x-auto border border-gray-100 dark:border-white/10 mb-10 max-w-full">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <table className="min-w-[900px] w-full divide-y divide-gray-100 dark:divide-white/5">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-2 py-3 w-8"></th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Varlık</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Miktar</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Alış</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Güncel</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Maliyet</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Değer</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Kar/Zarar</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Tarih</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">İşlem</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-white/5">
                  <SortableContext items={portfolio.map(p => p._id)} strategy={verticalListSortingStrategy}>
                    {portfolio.map((item) => (
                      <SortableRow
                        key={item._id}
                        item={item}
                        getTypeName={getTypeName}
                        deleteItem={deleteItem}
                        updateNote={updateNote}
                        formatPrice={formatPrice}
                        onCompare={setSelectedAssetForComparison}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </DndContext>
          </div>
        )}

        {/* Bottom Section */}
        {portfolio.length > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-white/10">
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6">Varlık Dağılımı</h3>
                <PortfolioChart portfolio={portfolio} />
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-white/10">
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6">Kar Simülasyonu</h3>
                <ProfitSimulator portfolio={portfolio} />
              </div>
            </div>

            <div className="mt-8 bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-white/10">
              <GoalsWidget totalPortfolioValue={stats ? stats.currentValue : 0} />
            </div>
          </>
        )}

        {selectedAssetForComparison && (
          <AssetComparison
            asset={selectedAssetForComparison}
            onClose={() => setSelectedAssetForComparison(null)}
          />
        )}
      </div>
    </div >
  );
};

export default Portfolio;
